import React, { useCallback, useEffect, useMemo, useState } from 'react';

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'unused', label: 'Not opened' },
  { value: 'opened', label: 'Opened' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_agent', label: 'Pending agent' },
  { value: 'complete', label: 'Complete' },
  { value: 'expired', label: 'Expired' },
];
const TEMPLATE_OPTIONS = [
  { key: 'soa-only', label: 'SOA Only' },
  { key: 'soa-with-intake', label: 'SOA with Intake' },
  { key: 'intake-only', label: 'Intake Only' },
  { key: 'indfam-consent', label: 'Ind-Fam Consent' },
  { key: 'indfam-intake-only', label: 'Ind-Fam Intake Only' },
  { key: 'indfam-intake-with-consent', label: 'Ind-Fam Intake with Consent' },
  { key: 'renewal', label: 'Renewal', disabled: true },
];

function fmt(value) {
  if (!value) return '—';
  return String(value).replace('T', ' ').replace('Z', ' UTC');
}

function normalizeStatus(status) {
  if (status === 'completed') return 'complete';
  return status || 'unknown';
}

function statusLabel(status) {
  const normalized = normalizeStatus(status);
  const labels = {
    unused: 'Not opened',
    opened: 'Opened',
    submitted: 'Submitted',
    pending_agent: 'Pending agent',
    complete: 'Complete',
    expired: 'Expired',
    revoked: 'Revoked',
    unknown: 'Unknown',
  };
  return labels[normalized] || normalized.replace(/_/g, ' ');
}

function formLabel(row) {
  const type = row.formType || row.formConfiguration || row.configuration || '';
  const labels = {
    'soa-only': 'Scope of Appointment',
    'soa-with-intake': 'Medicare Packet',
    'intake-only': 'Medicare Intake',
    'medicare-intake': 'Medicare Intake',
    'consent-only': 'Individual/Family Consent',
    'indfam-consent': 'Individual/Family Consent',
    'indfam-intake': 'Individual/Family Packet',
    'indfam-intake-only': 'Individual/Family Intake',
    'indfam-with-consent': 'Individual/Family Packet',
    'indfam-intake-with-consent': 'Individual/Family Packet',
  };
  if (labels[type]) return labels[type];
  if (row.inviteUrl?.includes('consent=only')) return 'Individual/Family Consent';
  if (row.inviteUrl?.includes('medicareforms')) return 'Medicare Form';
  if (row.agentUrl && !row.inviteUrl) return 'Agent Signature';
  return type || '-';
}
function rowMatchesStatus(row, selectedStatus) {
  return !selectedStatus || normalizeStatus(row.status) === selectedStatus;
}

function rowMatchesQuery(row, searchText) {
  const q = searchText.trim().toLowerCase();
  if (!q) return true;
  const values = [
    row.RowKey,
    row.documentId,
    row.contactId,
    row.dealId,
    row.beneficiaryName,
    row.beneficiaryFirstName,
    row.beneficiaryLastName,
    row.email,
    row.phone,
    row.zip,
    row.formType,
    formLabel(row),
    row.status,
    row.inviteUrl,
    row.agentUrl,
    row.verificationUrl,
  ];
  return values.some((value) => String(value || '').toLowerCase().includes(q));
}

function filterRows(rows, searchText, selectedStatus) {
  return rows.filter(row => rowMatchesStatus(row, selectedStatus) && rowMatchesQuery(row, searchText));
}

function isComplete(row) {
  return normalizeStatus(row.status) === 'complete';
}

async function fetchJson(url, options) {
  console.log('[Forms Admin] fetch start', { url, options: options || null });
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  console.log('[Forms Admin] fetch complete', {
    url,
    status: res.status,
    ok: res.ok,
    headers: {
      contentType: res.headers.get('content-type'),
      functionName: res.headers.get('x-forms-admin-function'),
    },
    data,
  });
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export default function App() {
  const [tab, setTab] = useState('invites');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [invites, setInvites] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [templateKey, setTemplateKey] = useState('soa-only');
  const [template, setTemplate] = useState({ subject: '', body: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cleanupMode, setCleanupMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const counts = useMemo(() => ({
    invites: invites.length,
    documents: documents.length,
    open: invites.filter(x => x.status === 'opened').length,
    pending: documents.filter(x => x.status === 'pending_agent').length,
  }), [invites, documents]);

  const loadLists = useCallback(async function loadLists(deepSearch = false) {
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    params.set('limit', '500');
    if (deepSearch && query) params.set('q', query);
    if (deepSearch && status) params.set('status', status);
    try {
      const [inviteData, docData] = await Promise.all([
        fetchJson(`/api/invites?${params}`),
        fetchJson(`/api/documents?${params}`),
      ]);
      setInvites(inviteData.items || []);
      setDocuments(docData.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [query, status]);

  const loadTemplate = useCallback(async function loadTemplate() {
    try {
      const data = await fetchJson(`/api/email-template?key=${encodeURIComponent(templateKey)}`);
      setTemplate({
        subject: data.subject || '',
        body: data.body || '',
      });
    } catch (err) {
      setError(err.message);
    }
  }, [templateKey]);

  async function saveTemplate() {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await fetchJson(`/api/email-template?key=${encodeURIComponent(templateKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
      });
      setMessage('Email template saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteRow(row) {
    const isInvite = tab === 'invites';
    const id = isInvite ? row.RowKey : (row.documentId || row.RowKey);
    if (!id) {
      setError('No record id found to delete.');
      return;
    }
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      setMessage('Click delete one more time to confirm.');
      return;
    }
    setError('');
    setMessage('');
    try {
      await fetchJson(`/api/${isInvite ? 'invites' : 'documents'}?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      setMessage(`${isInvite ? 'Invite' : 'Document'} deleted.`);
      setDeleteConfirm('');
      await loadLists(false);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    loadLists(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const activeRows = useMemo(() => filterRows(tab === 'invites' ? invites : documents, query, status), [documents, invites, query, status, tab]);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brandMark">TI</div>
          <div>
            <div className="brandName">Forms Admin</div>
            <div className="brandSub">Translating Insurance</div>
          </div>
        </div>
        <button className={tab === 'invites' ? 'nav active' : 'nav'} onClick={() => setTab('invites')}>Invites</button>
        <button className={tab === 'documents' ? 'nav active' : 'nav'} onClick={() => setTab('documents')}>Documents</button>
        <button className={tab === 'email' ? 'nav active' : 'nav'} onClick={() => setTab('email')}>Email Content</button>
      </aside>

      <main className="main">
        <header className="topbar">
          <div>
            <h1>{tab === 'email' ? 'Email Content' : tab === 'documents' ? 'SOA Documents' : 'Form Invites'}</h1>
            <p>Review form links, signing status, and outbound message content.</p>
          </div>
          <button className="primary" onClick={() => loadLists(false)} disabled={loading}>{loading ? 'Refreshing...' : 'Refresh'}</button>
        </header>

        <section className="metrics">
          <div><span>{counts.invites}</span><label>Invites Loaded</label></div>
          <div><span>{counts.open}</span><label>Opened Invites</label></div>
          <div><span>{counts.pending}</span><label>Pending Agent</label></div>
          <div><span>{counts.documents}</span><label>Documents Loaded</label></div>
        </section>

        {error && <div className="alert error">{error}</div>}
        {message && <div className="alert ok">{message}</div>}

        {tab !== 'email' && (
          <>
            <section className="filters">
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search name, contact, deal, document, email" />
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <button className="primary" onClick={() => loadLists(true)}>Search</button>
              <button
                className={cleanupMode ? 'ghost dangerOn' : 'ghost'}
                onClick={() => {
                  setCleanupMode(value => !value);
                  setDeleteConfirm('');
                }}
              >
                Cleanup
              </button>
            </section>

            <section className="tableWrap">
              <table>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Deal</th>
                    <th>Form</th>
                    <th>{tab === 'invites' ? 'Invite / Agent Link' : 'Document'}</th>
                    <th>Updated</th>
                    {cleanupMode && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {activeRows.map(row => (
                    <tr key={row.RowKey || row.documentId}>
                      <td><span className={`pill ${normalizeStatus(row.status)}`}>{statusLabel(row.status)}</span></td>
                      <td>
                        <strong>{row.beneficiaryName || row.beneficiaryFirstName || '—'}</strong>
                        <small>{row.email || row.phone || row.zip || ''}</small>
                      </td>
                      <td>{row.contactId || '—'}</td>
                      <td>{row.dealId || '—'}</td>
                      <td>{formLabel(row)}</td>
                      <td>
                        {isComplete(row) ? (
                          '�'
                        ) : tab === 'invites' ? (
                          <>
                            <CopyLink value={row.inviteUrl} label="Invite" />
                            <OpenLink value={row.agentUrl} label="Agent" />
                          </>
                        ) : (
                          <>
                            <OpenLink value={row.agentUrl} label="Agent" />
                            <CopyLink value={row.verificationUrl} label="Verify" />
                          </>
                        )}
                      </td>
                      <td>{fmt(row.updatedAt || row.createdAt || row.clientTimestamp)}</td>
                      {cleanupMode && (
                        <td className="cleanupCell">
                          <button
                            className={deleteConfirm === (tab === 'invites' ? row.RowKey : (row.documentId || row.RowKey)) ? 'deleteBtn confirm' : 'deleteBtn'}
                            onClick={() => deleteRow(row)}
                          >
                            {deleteConfirm === (tab === 'invites' ? row.RowKey : (row.documentId || row.RowKey)) ? 'Confirm' : 'Delete'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!activeRows.length && (
                    <tr><td colSpan={cleanupMode ? 8 : 7} className="empty">No records found.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          </>
        )}

        {tab === 'email' && (
          <section className="editor">
            <label>Template</label>
            <select value={templateKey} onChange={e => setTemplateKey(e.target.value)}>
              {TEMPLATE_OPTIONS.map(option => (
                <option key={option.key} value={option.key} disabled={option.disabled}>
                  {option.label}{option.disabled ? ' (not active yet)' : ''}
                </option>
              ))}
            </select>
            <label>Subject</label>
            <input value={template.subject} onChange={e => setTemplate(t => ({ ...t, subject: e.target.value }))} />
            <label>Body</label>
            <textarea rows={14} value={template.body} onChange={e => setTemplate(t => ({ ...t, body: e.target.value }))} />
            <div className="hint">Available merge fields: {'{{firstName}}'}, {'{{lastName}}'}, {'{{agentName}}'}, {'{{agentEmail}}'}, {'{{inviteUrl}}'}, {'{{expiresAt}}'}</div>
            <button className="primary" onClick={saveTemplate} disabled={saving}>{saving ? 'Saving...' : 'Save Template'}</button>
          </section>
        )}
      </main>
    </div>
  );
}

function OpenLink({ value, label }) {
  if (!value) return null;
  return <a className="linkBtn" href={value} target="_blank" rel="noreferrer">{label}</a>;
}

function CopyLink({ value, label }) {
  if (!value) return null;
  async function copy() {
    await navigator.clipboard.writeText(value);
  }
  return <button className="linkBtn" onClick={copy}>{label}</button>;
}
